// src/components/PlacesSearch.js
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import usePlacesAutocomplete from '../hooks/usePlacesAutocomplete';
import { getPlaceDetails } from '../services/placesService';

const SEARCH_FAILURE_MESSAGE = "We couldn't open that place. Try a more specific search.";

function hasUsableLocation(place) {
    const location = place?.geometry?.location;
    return (
        location &&
        typeof location.lat === "function" &&
        typeof location.lng === "function"
    );
}

export default function PlacesSearch({ value, onChange, onSelect }) {
    const inputRef = useRef(null);
    const syncPacGeometryRef = useRef(() => {});
    const [searchError, setSearchError] = useState("");
    const autocomplete = usePlacesAutocomplete(inputRef, {
        fields: [
            'place_id',
            'name',
            'formatted_address',
            'geometry',
            'address_components',
            'photos',
            'rating',
            'opening_hours',
        ],
    });

    // when user picks from dropdown
    useEffect(() => {
        if (!autocomplete) return;
        const listener = autocomplete.addListener('place_changed', async () => {
            const acPlace = autocomplete.getPlace();
            const placeId = String(acPlace?.place_id || "").trim();
            if (!placeId) {
                setSearchError(SEARCH_FAILURE_MESSAGE);
                return;
            }

            try {
                const full = await getPlaceDetails(placeId, [
                    'place_id',
                    'name',
                    'formatted_address',
                    'geometry',
                    'address_components',
                    'photos',
                    'rating',
                    'opening_hours',
                ]);
                if (!hasUsableLocation(full)) {
                    throw new Error("Place details missing geometry");
                }
                setSearchError("");
                onSelect(full);
            } catch (err) {
                console.warn('Failed to load place details', err);
                setSearchError(SEARCH_FAILURE_MESSAGE);
            }
        });
        return () => listener.remove();
    }, [autocomplete, onSelect]);

    useEffect(() => {
        const syncPacGeometry = () => {
            const inputEl = inputRef.current;
            if (!inputEl) return;

            const root = inputEl.closest(".MuiOutlinedInput-root");
            if (!root) return;

            const rect = root.getBoundingClientRect();
            if (!rect?.width) return;

            const viewportWidth =
                window.visualViewport?.width ||
                window.innerWidth ||
                document.documentElement.clientWidth;

            const width = Math.min(viewportWidth - 20, rect.width);
            const unclampedLeft = rect.left;
            const maxLeft = Math.max(10, viewportWidth - width - 10);
            const left = Math.min(maxLeft, Math.max(10, unclampedLeft));
            const viewportOffsetTop = window.visualViewport?.offsetTop || 0;
            const top = rect.bottom + viewportOffsetTop + 8;

            document.documentElement.style.setProperty("--inssa-pac-width", `${Math.round(width)}px`);
            document.documentElement.style.setProperty("--inssa-pac-left", `${Math.round(left)}px`);
            document.documentElement.style.setProperty("--inssa-pac-top", `${Math.round(top)}px`);
        };

        syncPacGeometryRef.current = syncPacGeometry;
        syncPacGeometry();

        const onResize = () => syncPacGeometry();
        const onScroll = () => syncPacGeometry();
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", onResize);
            window.visualViewport.addEventListener("scroll", onScroll);
        }

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", onResize);
                window.visualViewport.removeEventListener("scroll", onScroll);
            }
        };
    }, []);

    return (
        <TextField
            inputRef={inputRef}
            value={value}
            onChange={e => {
                if (searchError) setSearchError("");
                onChange(e.target.value);
                syncPacGeometryRef.current();
            }}
            placeholder="Search for any place…"
            size="small"
            variant="outlined"
            fullWidth
            error={Boolean(searchError)}
            helperText={searchError}
            onFocus={() => syncPacGeometryRef.current()}
            sx={(theme) => {
                const isDark = theme.palette.mode === "dark";
                const neutralBorder = isDark ? alpha(theme.palette.text.secondary, 0.42) : theme.colors.blue;
                const hoverBorder = isDark ? alpha(theme.palette.text.secondary, 0.62) : theme.colors.blue;
                const focusBorder = theme.colors.blue;
                return {
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "50px",
                    bgcolor: "background.paper",
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "50px",
                        color: "text.primary",
                        backgroundColor: "background.paper",
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: neutralBorder,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: hoverBorder,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: focusBorder,
                            borderWidth: 2,
                        },
                    },
                    "& .MuiOutlinedInput-input": {
                        padding: "12px 20px 12px 20px !important",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                };
            }}


            InputProps={{
                endAdornment: value && (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setSearchError("");
                                onChange('');
                            }}
                            sx={(theme) => ({
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                    color: theme.palette.text.primary,
                                },
                            })}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}

PlacesSearch.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};
